<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useGameStore, geoToSvg, COLORS } from '../stores/game'
import MapPopup from './MapPopup.ce.vue'
import NotificationOptIn from './NotificationOptIn.ce.vue'
import netherlandsSvgUrl from '../assets/netherlands.svg?url'

const store = useGameStore()

// Zoom & pan state
const zoomLevel = ref(1)
const panX = ref(0)
const panY = ref(0)
const isPanning = ref(false)
let panStartX = 0
let panStartY = 0

const mapContainer = ref(null)
const mapZoomWrapper = ref(null)
const dotOverlay = ref(null)
const svgWidth = ref(500)

// Popup state
const popupVisible = ref(false)
const popupTitle = ref('')
const popupDesc = ref('')
const popupLink = ref('')
const popupLeft = ref(0)
const popupTop = ref(0)
const popupIsSearch = ref(false)

const wrapperStyle = computed(() => ({
  transform: `translate(${panX.value}px, ${panY.value}px) scale(${zoomLevel.value})`,
  transformOrigin: '0 0',
}))

// Build per-player flag index for all visible flags
const indexedFlags = computed(() => {
  const counters = { mattie: 0, marieke: 0 }
  return store.allFlags.map((f) => {
    const player = f.winner?.toLowerCase()
    const idx = counters[player] ?? 0
    if (player in counters) counters[player]++
    return { ...f, playerFlagIndex: idx }
  })
})

// Map challenge flags to their 1-based index in sorted order
const challengeIndexMap = computed(() => {
  const map = new Map()
  store.sortedChallenges.forEach((c, i) => {
    const key = `${c.lat},${c.lng}`
    map.set(key, i + 1)
  })
  return map
})

// Route lines between challenge flags sorted by visible_at
const routeLines = computed(() => {
  const sorted = store.sortedChallenges
  const lines = []
  for (let i = 0; i < sorted.length - 1; i++) {
    const a = geoToSvg(sorted[i].lat, sorted[i].lng)
    const b = geoToSvg(sorted[i + 1].lat, sorted[i + 1].lng)
    const mx = (a.x + b.x) / 2
    const my = (a.y + b.y) / 2
    const dx = b.x - a.x
    const dy = b.y - a.y
    const len = Math.sqrt(dx * dx + dy * dy)
    const sign = i % 2 === 0 ? 1 : -1
    const offset = len * 0.4
    const cx = mx + sign * (-dy / len) * offset
    const cy = my + sign * (dx / len) * offset
    lines.push(`M${a.x},${a.y} Q${cx},${cy} ${b.x},${b.y}`)
  }
  return lines
})

function isHighlighted(flag) {
  return store.activeFlagPlayer === flag.winner?.toLowerCase() && store.activeFlagIndex === flag.playerFlagIndex
}

function dotRadius(flag) {
  if (isHighlighted(flag)) return 30
  return 20
}

function squareSize(flag) {
  if (isHighlighted(flag)) return 15
  return 12
}

function dotColor(flag) {
  return COLORS[flag.winner?.toLowerCase()] || '#888'
}

function showPopupAtScreenPoint(flag, screenX, screenY) {
  const container = mapContainer.value
  if (!container) return
  const rect = container.getBoundingClientRect()
  const clickX = screenX - rect.left
  const clickY = screenY - rect.top

  const popupWidth = 280
  let left = clickX - popupWidth / 2
  let top = clickY + 16

  // Clamp horizontally
  const margin = 8
  if (left < margin) left = margin
  if (left + popupWidth > rect.width - margin) left = rect.width - margin - popupWidth

  // If it goes below the container, show above the dot instead
  if (top + 120 > rect.height) top = clickY - 130
  if (top < 0) top = margin

  popupTitle.value = flag.label
  popupDesc.value = flag.description
  popupLink.value = flag.link || ''
  popupIsSearch.value = flag.type === 'search'
  popupLeft.value = left
  popupTop.value = top
  popupVisible.value = true
}

function onDotClick(flag, event) {
  event.stopPropagation()
  store.selectFlag(flag.winner?.toLowerCase(), flag.playerFlagIndex)
  showPopupAtScreenPoint(flag, event.clientX, event.clientY)
}

// When selection changes from scoreboard, show popup for that flag
watch(
  () => [store.activeFlagPlayer, store.activeFlagIndex],
  ([player, index]) => {
    if (!player || index < 0) return

    const svg = dotOverlay.value
    const container = mapContainer.value
    if (!svg || !container) return

    const flag = indexedFlags.value.find((f) => f.winner?.toLowerCase() === player && f.playerFlagIndex === index)
    if (!flag) return

    const pos = geoToSvg(flag.lat, flag.lng)
    const pt = svg.createSVGPoint()
    pt.x = pos.x
    pt.y = pos.y
    const screenPt = pt.matrixTransform(svg.getScreenCTM())
    showPopupAtScreenPoint(flag, screenPt.x, screenPt.y)
  }
)

function closePopup() {
  popupVisible.value = false
}

// Zoom
function zoomAtPoint(newZoom, clientX, clientY) {
  newZoom = Math.min(Math.max(newZoom, 0.5), 3)
  const rect = mapContainer.value.getBoundingClientRect()
  const wrapper = mapZoomWrapper.value
  const px = clientX - rect.left - wrapper.offsetLeft
  const py = clientY - rect.top - wrapper.offsetTop
  panX.value = px - (px - panX.value) * (newZoom / zoomLevel.value)
  panY.value = py - (py - panY.value) * (newZoom / zoomLevel.value)
  zoomLevel.value = newZoom
}

function resetView() {
  zoomLevel.value = 1
  panX.value = 0
  panY.value = 0
  closePopup()
  store.clearSelection()
}

function zoomIn() {
  const rect = mapContainer.value.getBoundingClientRect()
  zoomAtPoint(zoomLevel.value + 0.25, rect.left + rect.width / 2, rect.top + rect.height / 2)
}

function zoomOut() {
  const rect = mapContainer.value.getBoundingClientRect()
  zoomAtPoint(zoomLevel.value - 0.25, rect.left + rect.width / 2, rect.top + rect.height / 2)
}

function onWheel(e) {
  e.preventDefault()
  const delta = e.deltaY > 0 ? -0.05 : 0.05
  zoomAtPoint(zoomLevel.value + delta, e.clientX, e.clientY)
}

// Pan
function onMouseDown(e) {
  if (e.target.closest('.zoom-btn') || e.target.closest('.dot')) return
  isPanning.value = true
  panStartX = e.clientX - panX.value
  panStartY = e.clientY - panY.value
}

function onMouseMove(e) {
  if (!isPanning.value) return
  panX.value = e.clientX - panStartX
  panY.value = e.clientY - panStartY
}

function onMouseUp() {
  isPanning.value = false
}

function onContainerClick(e) {
  if (!e.target.closest('.dot') && !e.target.closest('.popup')) {
    closePopup()
    store.clearSelection()
  }
}

// Touch support for mobile
let lastTouchDist = 0
let lastTouchCenter = { x: 0, y: 0 }

function getTouchDistance(touches) {
  const dx = touches[0].clientX - touches[1].clientX
  const dy = touches[0].clientY - touches[1].clientY
  return Math.sqrt(dx * dx + dy * dy)
}

function getTouchCenter(touches) {
  return {
    x: (touches[0].clientX + touches[1].clientX) / 2,
    y: (touches[0].clientY + touches[1].clientY) / 2,
  }
}

function onTouchStart(e) {
  if (e.touches.length === 2) {
    e.preventDefault()
    lastTouchDist = getTouchDistance(e.touches)
    lastTouchCenter = getTouchCenter(e.touches)
  } else if (e.touches.length === 1) {
    if (e.target.closest('.zoom-btn') || e.target.closest('.dot')) return
    isPanning.value = true
    panStartX = e.touches[0].clientX - panX.value
    panStartY = e.touches[0].clientY - panY.value
  }
}

function onTouchMove(e) {
  if (e.touches.length === 2) {
    e.preventDefault()
    const dist = getTouchDistance(e.touches)
    const center = getTouchCenter(e.touches)
    const scale = dist / lastTouchDist
    zoomAtPoint(zoomLevel.value * scale, center.x, center.y)
    // Also pan with pinch center movement
    panX.value += center.x - lastTouchCenter.x
    panY.value += center.y - lastTouchCenter.y
    lastTouchDist = dist
    lastTouchCenter = center
  } else if (e.touches.length === 1 && isPanning.value) {
    panX.value = e.touches[0].clientX - panStartX
    panY.value = e.touches[0].clientY - panStartY
  }
}

function onTouchEnd() {
  isPanning.value = false
  lastTouchDist = 0
}

function updateSvgWidth() {
  const container = mapContainer.value
  if (container) {
    svgWidth.value = container.clientWidth * 0.95
  }
}

onMounted(() => {
  window.addEventListener('mousemove', onMouseMove)
  window.addEventListener('mouseup', onMouseUp)
  updateSvgWidth()
})

onUnmounted(() => {
  window.removeEventListener('mousemove', onMouseMove)
  window.removeEventListener('mouseup', onMouseUp)
})
</script>

<template>
  <div
    ref="mapContainer"
    class="map-container"
    :class="{ panning: isPanning }"
    @mousedown="onMouseDown"
    @wheel.prevent="onWheel"
    @click="onContainerClick"
    @touchstart="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd"
  >
    <div ref="mapZoomWrapper" class="map-zoom-wrapper" :style="wrapperStyle">
      <svg ref="dotOverlay" class="map-svg" :style="{ width: svgWidth + 'px' }" viewBox="0 0 887.641 1000.512">
        <image :href="netherlandsSvgUrl" width="887.641" height="1000.512" />

        <!-- Route lines -->
        <path
          v-for="(d, i) in routeLines"
          :key="'route-' + i"
          :d="d"
          fill="none"
          stroke="#888"
          stroke-width="1.5"
          stroke-dasharray="6,4"
        />

        <!-- Flag dots -->
        <g
          v-for="(flag, i) in indexedFlags"
          :key="'dot-' + i"
          class="dot"
          :class="{ highlighted: isHighlighted(flag) }"
          @click="onDotClick(flag, $event)"
        >
          <!-- Challenge: circle -->
          <circle
            v-if="flag.type !== 'search'"
            :cx="geoToSvg(flag.lat, flag.lng).x"
            :cy="geoToSvg(flag.lat, flag.lng).y"
            :r="dotRadius(flag)"
            :fill="dotColor(flag)"
            stroke="#fff"
            :stroke-width="isHighlighted(flag) ? 3 : 2"
          />
          <!-- Search: square -->
          <rect
            v-if="flag.type === 'search'"
            :x="geoToSvg(flag.lat, flag.lng).x - squareSize(flag)"
            :y="geoToSvg(flag.lat, flag.lng).y - squareSize(flag)"
            :width="squareSize(flag) * 2"
            :height="squareSize(flag) * 2"
            :rx="4"
            :fill="dotColor(flag)"
            stroke="#fff"
            :stroke-width="isHighlighted(flag) ? 3 : 2"
          />
          <!-- Challenge index letter -->
          <text
            v-if="flag.type !== 'search'"
            :x="geoToSvg(flag.lat, flag.lng).x"
            :y="geoToSvg(flag.lat, flag.lng).y + 6"
            text-anchor="middle"
            font-size="20"
            font-weight="bold"
            fill="#fff"
            pointer-events="none"
          >
            {{ String.fromCharCode(64 + challengeIndexMap.get(`${flag.lat},${flag.lng}`)) }}
          </text>
          <!-- Search: Z label -->
          <text
            v-if="flag.type === 'search'"
            :x="geoToSvg(flag.lat, flag.lng).x"
            :y="geoToSvg(flag.lat, flag.lng).y + 5"
            text-anchor="middle"
            font-size="14"
            font-weight="bold"
            fill="#fff"
            pointer-events="none"
          >
            Z
          </text>
        </g>
      </svg>
    </div>

    <MapPopup
      :title="popupTitle"
      :description="popupDesc"
      :link="popupLink"
      :isSearch="popupIsSearch"
      :visible="popupVisible"
      :left="popupLeft"
      :top="popupTop"
      @close="closePopup"
    />

    <NotificationOptIn />

    <div class="zoom-controls">
      <button class="zoom-btn" @click.stop="zoomIn">+</button>
      <button class="zoom-btn" @click.stop="zoomOut">&minus;</button>
      <button class="zoom-btn reset-btn" title="Reset kaart" @click.stop="resetView">&#8634;</button>
    </div>
  </div>
</template>

<style scoped>
.map-container {
  position: relative;
  overflow: hidden;
  cursor: grab;
  background: rgba(0, 0, 0, 0.05);
  z-index: 2;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  touch-action: none;
}

.map-container.panning {
  cursor: grabbing;
}

.map-zoom-wrapper {
  transform-origin: 0 0;
  position: relative;
}

.map-svg {
  display: block;
  height: auto;
}

.dot {
  pointer-events: all;
  cursor: pointer;
}

.dot:hover circle {
  r: 22;
}

.dot.highlighted circle {
  r: 30;
  stroke: #ffe066;
  stroke-width: 3;
}

.zoom-controls {
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  gap: 4px;
  z-index: 5;
}

.zoom-btn {
  width: 40px;
  height: 40px;
  background: rgba(0, 0, 0, 0.4);
  border: none;
  border-radius: 6px;
  color: #fff;
  font-family: var(--font-body);
  font-size: 2rem;
  font-weight: 900;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  user-select: none;
}

.zoom-btn:hover {
  background: rgba(0, 0, 0, 0.6);
}
</style>
